import { useEffect } from 'react';

import { authQueries } from '@/api/common/auth';
import { useAuthStore } from '@/stores/useAuthStore';
import { useQuery } from '@tanstack/react-query';

/**
 * PROTOTYPE / MOCK MODE / MOCK AUTH ONLY
 *
 * Replaces ClerkProvider + AuthProvider + OrganizationProvider + ClerkAxiosSetup
 * when VITE_MOCK_MODE (MSW-intercepted /api/site/whoami) or VITE_MOCK_AUTH
 * (real backend /api/site/whoami, identity handled by the gateway) is set.
 * Populates the auth store directly - no Clerk dependency.
 */

interface MockAuthProviderProps {
  children: React.ReactNode;
}

export const MockAuthProvider: React.FC<MockAuthProviderProps> = ({ children }) => {
  const { setClerkAuth, setUser, setLoadingUserData } = useAuthStore();

  const {
    data: whoAmIData,
    isLoading,
    error
  } = useQuery({
    ...authQueries.whoAmI(),
    enabled: true
  });

  useEffect(() => {
    // In mock mode, we're always "signed in" and "loaded"
    setClerkAuth(true, true);

    if (isLoading) {
      setLoadingUserData(true);
      return;
    }

    if (error || !whoAmIData) {
      setUser(null);
    } else {
      setUser(whoAmIData.Security.User);
    }
  }, [whoAmIData, isLoading, error, setClerkAuth, setUser, setLoadingUserData]);

  return <>{children}</>;
};
